import React from 'react';
interface InvoiceHeaderAltProps {
  company: {
    name: string;
    address: string[];
    uen: string;
    gst: string;
    travelLicense?: string;
    logoUrl?: string;
  };
  invoice: {
    number: string;
    issueDate: string;
    bookingRef: string;
    dueDate: string;
    salesStaff: string;
    bookingStaff: string;
    productName: string;
    printedDate: string;
    lastUpdateDate: string;
  };
  billTo: {
    name: string;
    address: string[];
    attn?: string;
  };
  pageNumber?: number;
  totalPages?: number;
  isFirstPage?: boolean;
  isVoidVersion?: boolean;
}
export function InvoiceHeaderAlt({
  company,
  invoice,
  billTo,
  pageNumber = 1,
  totalPages = 1,
  isFirstPage = true,
  isVoidVersion = false
}: InvoiceHeaderAltProps) {
  return <>
      {/* Top Row: Logo Left, Address Right */}
      <div className="flex justify-between items-start mb-4" data-id="element-554">
        {/* Left: Logo */}
        <div data-id="element-555">
          {company.logoUrl ? <img src={company.logoUrl} alt={company.name} className="h-14 object-contain object-left" data-id="element-556" /> : <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md" data-id="element-557">
              <span className="text-white font-bold text-lg" data-id="element-558">PA</span>
            </div>}
        </div>

        {/* Right: Company Address - Compact */}
        <div className="text-right" data-id="element-559">
          <h1 className="text-sm text-neutral-900 mb-1" data-id="element-560">{company.name}</h1>
          <div className="text-[10px] text-neutral-500 space-y-0.5 leading-relaxed" data-id="element-561">
            {company.address.map((line, i) => <p key={i} data-id="element-562">{line}</p>)}
            <p className="mt-1" data-id="element-563">
              UEN: {company.uen} | GST: {company.gst}
            </p>
            {company.travelLicense && <p data-id="element-564">Travel Agent License: {company.travelLicense}</p>}
          </div>
        </div>
      </div>

      {/* TAX INVOICE Title - Centered Above Box */}
      <h2 className="text-lg font-bold text-neutral-900 mb-2 tracking-tight text-center" data-id="element-565">
        TAX INVOICE
      </h2>

      {/* Invoice Details Box - Two Columns */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 mb-4" data-id="element-566">
        {/* Two Column Grid - Reorganized */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5" data-id="element-567">
          {/* Left Column */}
          <div className="space-y-1.5" data-id="element-568">
            <div className="flex justify-between" data-id="element-569">
              <span className="text-xs font-medium text-neutral-600" data-id="element-570">
                Invoice No.:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-571">
                {invoice.number}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-572">
              <span className="text-xs font-medium text-neutral-600" data-id="element-573">
                Issue Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-574">
                {invoice.issueDate}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-575">
              <span className="text-xs font-medium text-neutral-600" data-id="element-576">
                Booking Ref:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-577">
                {invoice.bookingRef}
              </span>
            </div>
            {isFirstPage && <div className="flex justify-between" data-id="element-578">
                <span className="text-xs font-medium text-neutral-600" data-id="element-579">
                  Product Name:
                </span>
                <span className="text-xs text-neutral-900" data-id="element-580">
                  {invoice.productName}
                </span>
              </div>}
          </div>

          {/* Right Column */}
          <div className="space-y-1.5" data-id="element-581">
            <div className="flex justify-between" data-id="element-582">
              <span className="text-xs font-medium text-neutral-600" data-id="element-583">
                Due Date:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-584">
                {invoice.dueDate}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-585">
              <span className="text-xs font-medium text-neutral-600" data-id="element-586">
                Printed Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-587">
                {invoice.printedDate}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-588">
              <span className="text-xs font-medium text-neutral-600" data-id="element-589">
                Last Update:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-590">
                {invoice.lastUpdateDate}
              </span>
            </div>
            {isFirstPage && <>
                <div className="flex justify-between" data-id="element-591">
                  <span className="text-xs font-medium text-neutral-600" data-id="element-592">
                    Sales Staff:
                  </span>
                  <span className="text-xs font-semibold text-neutral-900" data-id="element-593">
                    {invoice.salesStaff}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-594">
                  <span className="text-xs font-medium text-neutral-600" data-id="element-595">
                    Booking Staff:
                  </span>
                  <span className="text-xs font-semibold text-neutral-900" data-id="element-596">
                    {invoice.bookingStaff}
                  </span>
                </div>
              </>}
          </div>
        </div>
      </div>

      {/* Bill To Section - Only on first page */}
      {isFirstPage && <div className={`mb-6 border border-neutral-200 rounded-lg p-4 ${isVoidVersion ? 'bg-white' : 'bg-neutral-50'}`} data-id="element-597">
          <p className="text-sm font-bold text-neutral-900 mb-2 uppercase tracking-wide" data-id="element-598">
            Bill To:
          </p>
          <div className="space-y-1" data-id="element-599">
            <p className="text-base font-semibold text-neutral-900" data-id="element-600">
              {billTo.name}
            </p>
            <div className="text-sm text-neutral-700 space-y-0.5" data-id="element-601">
              {billTo.address.map((line, i) => <p key={i} data-id="element-602">{line}</p>)}
            </div>
            {billTo.attn && <p className="text-sm text-neutral-700 mt-2" data-id="element-603">
                Attn: {billTo.attn}
              </p>}
          </div>
        </div>}
    </>;
}